import React, { useEffect, useMemo, useState } from "react";
import { AnnounceModel, pageTypeOptions, ridTypeOptions } from "./Announce";
import Client from "../../models/client";
import { Box, Button, Group, ScrollArea, Select, Stack, Textarea, TextInput, Tooltip } from "@mantine/core";
import { QuestionIcon } from "@phosphor-icons/react";
import { useForm } from "@mantine/form";
import { createAnnoune, updateAnnounce } from "../api";
import { notifications } from "@mantine/notifications";
import { BaseModal } from "./BaseModal";
import { confirmDiscardChanges } from "../utils/confirm";

interface Props {
  opened: boolean
  onClose: () => void
  announce: AnnounceModel | null
  client: Client
  onSaved: () => void
}

interface FormValues {
  page_type: string
  rid: string
  rid2: string
  rid3: string
  sales_id: string
  campaign_id: string
  note: string
}

const defaultValues: FormValues = {
  page_type: 'invite',
  rid: 'crm',
  rid2: '',
  rid3: '',
  sales_id: '',
  campaign_id: '',
  note: '',
}

const ALPHANUMERIC = /^[0-9a-zA-Z]+$/
const validateAlphanumeric = (value: string) =>
  !value || ALPHANUMERIC.test(value) ? null : '半角英数字で入力してください'

const helperLabels: { name: keyof FormValues; help: string }[] = [
  {
    name: 'rid2',
    help: 'メルマガやLINEリッチメニューなど、メディア名ごとの判別にご利用ください。',
  },
  {
    name: 'rid3',
    help: 'ボタンの設置個所やバナーのサイズ、クリエイティブタイプ、配信日などの判別にご利用ください。',
  },
  {
    name: 'sales_id',
    help: '担当者名、担当者ID、店舗名、店舗コードなどの判別にご利用ください。',
  },
  {
    name: 'campaign_id',
    help: '季節や期間限定のイベントの開催に応じた判別にご利用ください。',
  },
]

const HelperLabel = ({ label, help }: { label: string; help: string }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
    {label}
    <Tooltip label={help} multiline w={260} withArrow>
      <span style={{ display: 'inline-flex', cursor: 'help' }}>
        <QuestionIcon size={16} color="#00000099" />
      </span>
    </Tooltip>
  </span>
)

export const AnnounceEditUrlForm = ({
  opened,
  onClose,
  announce,
  client,
  onSaved,
}: Props) => {
  const [submitting, setSubmitting] = useState(false)

  const form = useForm<FormValues>({
    initialValues: defaultValues,
    validate: {
      rid2: validateAlphanumeric,
      rid3: validateAlphanumeric,
      sales_id: validateAlphanumeric,
      campaign_id: validateAlphanumeric,
    },
  })

  useEffect(() => {
    if (!opened) return
    if (announce) {
      form.setValues({
        page_type: announce.page_type ?? 'invite',
        rid: announce.rid ?? 'crm',
        rid2: announce.rid2 ?? '',
        rid3: announce.rid3 ?? '',
        sales_id: announce.sales_id ?? '',
        campaign_id: announce.campaign_id ?? '',
        note: announce.note ?? ''
      })
    } else {
      form.setValues(defaultValues)
    }
    form.resetDirty()
  }, [opened, announce])

  const handleClose = () => {
    if (
      form.isDirty() &&
      !confirmDiscardChanges()
    ) {
      return
    }
    onClose()
  }

  const ridSelectData = useMemo(
    () =>
      ridTypeOptions.map((o) => ({
        value: o.value,
        label: `${o.label}（${o.value}）`,
      })),
    [],
  )

  const handleSubmit = async (values: FormValues) => {
    setSubmitting(true)
    try {
      if (announce) {
        await updateAnnounce(client.id, announce.id, values)
        notifications.show({ message: '告知用URLを更新しました', color: 'teal' })
      } else {
        await createAnnoune(client.id, values)
        notifications.show({ message: '告知用URLを作成しました', color: 'teal' })
      }
      onSaved()
      onClose()
    } catch (e) {
      console.error(
        announce ? '告知用URLの更新に失敗しました' : '告知用URLの作成に失敗しました',
        e,
      )
      notifications.show({
        message: announce
          ? '告知用URLの更新に失敗しました'
          : '告知用URLの作成に失敗しました',
        color: 'red',
      })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <BaseModal
      opened={opened}
      onClose={handleClose}
      title={announce ? '告知用URL編集' : '告知用URL新規作成'}
      width={560}
      onExited={() => {
        form.setValues(defaultValues)
        form.resetDirty()
      }}
    >
      <form
        onSubmit={form.onSubmit(handleSubmit)}
        style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}
      >
        <ScrollArea style={{ flex: 1, minHeight: 0 }} bg="#00000008" px={24}>
          <Stack gap="md" p="md">
            <Select
              label="ページ種類"
              data={pageTypeOptions}
              allowDeselect={false}
              {...form.getInputProps('page_type')}
            />
            <Select
              label={
                <HelperLabel
                  label="rid"
                  help="LINEやTOPページ、CRMツールなど、参照元の判別にご利用ください"
                />
              }
              data={ridSelectData}
              allowDeselect={false}
              searchable
              {...form.getInputProps('rid')}
            />
            {helperLabels.map((h) => (
              <TextInput
                key={h.name}
                label={<HelperLabel label={h.name} help={h.help} />}
                {...form.getInputProps(h.name)}
              />
            ))}
            <Textarea label="メモ" autosize minRows={2} {...form.getInputProps('note')} />
          </Stack>
        </ScrollArea>

        <Box
          p="md"
          style={{
            borderTop: '1px solid #0000001A',
            backgroundColor: '#fff',
            flexShrink: 0,
          }}
        >
          <Group justify="flex-end" gap={8}>
            <Button
              variant="outline"
              radius="xl"
              color="gray"
              onClick={handleClose}
              disabled={submitting}
            >
              キャンセル
            </Button>
            <Button
              variant="filled"
              radius="xl"
              type="submit"
              loading={submitting}
            >
              保存
            </Button>
          </Group>
        </Box>
      </form>
    </BaseModal>
  )
}
